import { PropertyBusinessType } from 'models/Properties';
import {
  ConnectionConfigType,
  InternalIntegrationSetting,
  MuSupportType,
} from 'models/InternalIntegrationSetting';
import Switch from 'components/switch/Switch';
import Tooltip from 'components/tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import Badge from 'components/badge/Badge';
import theme from 'styles/theme';

interface ChannelToggleProps {
  integration: InternalIntegrationSetting;
  propertyBusinessType: PropertyBusinessType;
  isActive: boolean;
  handleToggle: () => void;
  isUpdating: boolean;
}

const ChannelToggle = ({
  integration,
  propertyBusinessType,
  isActive,
  handleToggle,
  isUpdating,
}: ChannelToggleProps) => {
  const { t } = useTranslation();

  const {
    integration: { muSupportType, connectionConfig },
  } = integration;

  const isOutboundOauth =
    connectionConfig.type === ConnectionConfigType.OUTBOUND_OAUTH;
  const isHotelProperty = propertyBusinessType === PropertyBusinessType.HOTEL;
  const isUnitTypeProperty =
    propertyBusinessType === PropertyBusinessType.UNIT_TYPE;

  const isHotelOrUnitTypeProperty = isHotelProperty || isUnitTypeProperty;
  const hasMUSupport = muSupportType !== MuSupportType.NONE;
  const isSupportedUnitTypeProperty = isUnitTypeProperty && hasMUSupport;

  if (isHotelOrUnitTypeProperty && !hasMUSupport) {
    return (
      <Badge
        backgroundColor={theme.colors.gray300}
        data-testid="no-multi-unit-support"
        fontColor={theme.colors.fontDark}
      >
        {t('pageProperty.channels.noMultiUnitSupport')}
      </Badge>
    );
  }

  // Hide the toggle for supported Hotel properties
  if (isHotelProperty && hasMUSupport) {
    return null;
  }

  // Supported unit types and Outbound Oauth channels should be activated directly in the channel details page
  if (isOutboundOauth || isSupportedUnitTypeProperty) {
    return (
      <Tooltip
        id="channel-item-toggle-tooltip"
        placement="bottom"
        text={t('pageProperty.channels.useGearIcon')}
      >
        <Switch data-testid="channel-item-toggle" checked={isActive} disabled />
      </Tooltip>
    );
  }

  return (
    <Switch
      data-testid="channel-item-toggle"
      checked={isActive}
      onClick={handleToggle}
      disabled={isUpdating}
    />
  );
};

export default ChannelToggle;
